import { css, jsx } from '@emotion/core';
import { Col, Form as AntForm, Icon, Input, Modal, Radio, Row, Select } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { saveSearch } from '@/src/compliance/utils';
import DateRangePicker from '@/src/shared/components/DateRangePicker';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import X from '@/src/shared/components/Icon/_icons/X';
import { bp, form as formStyle, space } from '@/styles';
import { convertMomentToDate, disableDayAfter, validateCustomRange } from '@/utils/dateTimeHelpers';
import { colMdProps, rowProps } from '@/utils/rowColHelpers';
import { multiSelectProps, singleSelectProps } from '@/utils/selectHelpers';

const { Option } = Select;

const { m } = space;

const style = {
  footerWrapper: css`
    display: flex;
    flex-direction: column;
    @media ${bp.md} {
      justify-content: space-between;
      flex-direction: row;
    }
  `,
  footerButtonWrapper: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    @media ${bp.md} {
      flex-direction: inherit;
      width: auto;
    }
  `,
  cancel: css`
    margin-top: 16px;
    text-align: center;

    @media ${bp.md} {
      margin-top: 0px;
    }
  `,
  cancelInner: css`
    display: flex !important;
    align-items: baseline !important;
    text-align: center !important;
    justify-content: center;
  `,
  cancelIcon: css`
    padding-left: 8px;
  `,
  cancelText: css`
    padding-left: 0px;
  `,
  saveSearch: css`
    margin-left: 0px !important;
    margin-top: 16px;
    @media ${bp.md} {
      margin-top: 0px;
      margin-left: 8px !important;
    }
  `,
};

const hasValue = value => {
  if (Array.isArray(value)) return value.length > 0;

  return value !== undefined && value !== null && value !== '';
};

const normalizeStates = states => {
  if (!Array.isArray(states)) return [];

  return states
    .map(state => {
      if (typeof state === 'object') return state.stateCode;

      return state;
    })
    .filter(Boolean);
};

const normalizeCharts = charts => {
  if (!Array.isArray(charts)) return [];

  return charts
    .map(chart => {
      if (typeof chart === 'object') return chart.chartId;

      return chart;
    })
    .filter(Boolean);
};

const normalizeCategory = categories => {
  if (Array.isArray(categories)) {
    if (!categories.length) return undefined;

    const category = categories[0];

    if (typeof category === 'object') return category.categoryId;

    return category;
  }

  if (categories && typeof categories === 'object') return categories.categoryId;

  return hasValue(categories) ? categories : undefined;
};

const normalizeDateRanges = ({ dateRanges, dateStart, dateEnd }) => {
  if (Array.isArray(dateRanges) && dateRanges[0] && dateRanges[1]) {
    return dateRanges;
  }

  if (dateStart && dateEnd) {
    return [moment(dateStart), moment(dateEnd)];
  }

  return [null, null];
};

const AddSavedSearch = props => {
  const {
    visible,
    closeModal,
    searchOptions,
    form,
    data = {},
    handleSaveSearch,
    includeDates = false,
    isRecentChanges = false,
  } = props;

  const { charts = [], categories = [], states = [] } = searchOptions;

  const personId = useSelector(state => state.auth.userinfo.PersonID);
  const searchFiltersValues = useSelector(state => state.compliance.searchFiltersValues);

  const dispatch = useDispatch();

  const currentFormValues = useMemo(() => {
    const searchText = hasValue(searchFiltersValues.searchText)
      ? searchFiltersValues.searchText
      : data.searchText;

    const selectedStates = hasValue(searchFiltersValues.states)
      ? searchFiltersValues.states
      : data.states;

    const selectedCategory = hasValue(searchFiltersValues.categories)
      ? searchFiltersValues.categories
      : data.categories;

    const selectedCharts = hasValue(searchFiltersValues.charts)
      ? searchFiltersValues.charts
      : data.charts;

    const dateStart = hasValue(searchFiltersValues.dateStart)
      ? searchFiltersValues.dateStart
      : data.dateStart;

    const dateEnd = hasValue(searchFiltersValues.dateEnd)
      ? searchFiltersValues.dateEnd
      : data.dateEnd;

    return {
      searchDescription: data.searchDescription || '',
      includeOnHome: data.includeOnHome || 'N',
      searchText: searchText || '',
      states: normalizeStates(selectedStates),
      categories: normalizeCategory(selectedCategory),
      charts: normalizeCharts(selectedCharts),
      dateRanges: normalizeDateRanges({
        dateRanges: data.dateRanges,
        dateStart,
        dateEnd,
      }),
    };
  }, [data, searchFiltersValues]);

  useEffect(() => {
    if (!visible) return;

    const fields = {
      searchDescription: currentFormValues.searchDescription,
      includeOnHome: currentFormValues.includeOnHome,
      searchText: currentFormValues.searchText,
      states: currentFormValues.states,
      categories: currentFormValues.categories,
      charts: currentFormValues.charts,
    };

    if (includeDates) {
      fields.dateRanges = currentFormValues.dateRanges;
    }

    form.resetFields();
    form.setFieldsValue(fields);
  }, [visible, currentFormValues, includeDates]);

  const onSave = async () => {
    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      const formattedValues = {
        ...values,
        dateStart: Array.isArray(values.dateRanges)
          ? convertMomentToDate(values.dateRanges[0])
          : null,
        dateEnd: Array.isArray(values.dateRanges)
          ? convertMomentToDate(values.dateRanges[1])
          : null,
      };

      const formattedPayload = saveSearch({
        personId,
        values: formattedValues,
        form,
        options: searchOptions,
        initialData: {
          ...data,
          personId,
        },
      });

      dispatch(handleSaveSearch({
        payload: formattedPayload,
        successCallback: closeModal,
      }));
    });
  };

  const handleCategoryChange = categoryId => {
    const chartValue = form.getFieldValue('charts');

    if (!Array.isArray(chartValue)) return;

    const hasInvalidChart = chartValue.some(chartId => {
      const chart = charts.find(ch => Number(ch.chartId) === Number(chartId));

      return !chart || !(chart.categories || [])
        .map(cat => cat.categoryId)
        .includes(categoryId);
    });

    if (hasInvalidChart) {
      form.setFieldsValue({ charts: [] });
    }
  };

  const renderMultiSelect = ({
    options,
    optCode,
    optDescription,
    label,
    _props = {},
    initialValue = [],
    field,
  }) => {
    const { getFieldDecorator } = form;

    return (
      <Col {...colMdProps}>
        <AntForm.Item label={label}>
          {getFieldDecorator(field, {
            initialValue,
          })(
            <Select {...multiSelectProps(false)} {..._props}>
              {(options || []).map(opt => (
                <Option value={opt[optCode]} key={`${opt[optCode]}-${label}`}>
                  {opt[optDescription]}
                </Option>
              ))}
            </Select>
          )}
        </AntForm.Item>
      </Col>
    );
  };

  const selectedCategoryValue = form.getFieldValue('categories') || currentFormValues.categories;

  const filteredCharts = selectedCategoryValue
    ? charts.filter(chart => {
        return (chart.categories || [])
          .map(category => category.categoryId)
          .includes(selectedCategoryValue);
      })
    : charts;

  return (
    <Modal
      visible={visible}
      onCancel={closeModal}
      width="80%"
      maskStyle={{
        backgroundColor: 'rgba(0,38,73,0.65)',
      }}
      title={
        isRecentChanges ? (
          <Heading type={3} weight="bolder" theme="gray" css={m.b0}>
            Save Search - Compliance Charts Recent Changes
          </Heading>
        ) : (
          <Heading type={3} weight="bolder" theme="gray" css={m.b0}>
            Save Search
          </Heading>
        )
      }
      footer={[
        <div css={style.footerWrapper} key="delete-button">
          <span css={style.footerButtonWrapper}>
            <Button
              theme="gray"
              size="small"
              css={style.cancel}
              disabled={false}
              onClick={closeModal}
            >
              <span css={style.cancelInner}>
                <span css={style.cancelIcon}>
                  <X css={style.cancelIcon} />
                </span>
                <span css={style.cancelIcon}>Cancel</span>
              </span>
            </Button>
            <Button
              disabled={false}
              size="small"
              theme="default"
              css={style.saveSearch}
              htmlType="submit"
              onClick={onSave}
            >
              <Icon type="save" theme="filled" />
              Save Search
            </Button>
          </span>
        </div>,
      ]}
    >
      <Form>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Name
            </Heading>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Name of Search">
              {form.getFieldDecorator('searchDescription', {
                initialValue: currentFormValues.searchDescription,
                rules: [
                  {
                    message: 'Enter Name of Search.',
                    required: true,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>

          <Col {...colMdProps}>
            <AntForm.Item label="Show on homepage">
              {form.getFieldDecorator('includeOnHome', {
                initialValue: currentFormValues.includeOnHome,
              })(
                <Radio.Group>
                  <Radio value="Y">Yes</Radio>
                  <Radio value="N">No</Radio>
                </Radio.Group>
              )}
            </AntForm.Item>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Filters
            </Heading>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Search Text">
              {form.getFieldDecorator('searchText', {
                initialValue: currentFormValues.searchText,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>

          {renderMultiSelect({
            label: 'State',
            options: states,
            optCode: 'stateCode',
            optDescription: 'stateDescription',
            field: 'states',
            initialValue: currentFormValues.states,
          })}
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Category">
              {form.getFieldDecorator('categories', {
                initialValue: currentFormValues.categories,
              })(
                <Select
                  {...singleSelectProps(false)}
                  onChange={categoryId => {
                    handleCategoryChange(categoryId);
                  }}
                >
                  {(categories || []).map(category => (
                    <Option value={category.categoryId} key={`${category.categoryId}-category`}>
                      {category.categoryDescription}
                    </Option>
                  ))}
                </Select>
              )}
            </AntForm.Item>
          </Col>

          {renderMultiSelect({
            label: 'Charts',
            options: filteredCharts,
            optCode: 'chartId',
            optDescription: 'chartTitle',
            field: 'charts',
            initialValue: currentFormValues.charts,
          })}
        </Row>

        {includeDates && (
          <Row {...rowProps}>
            <Col {...colMdProps}>
              <AntForm.Item label="Date Range">
                {form.getFieldDecorator('dateRanges', {
                  initialValue: currentFormValues.dateRanges,
                  rules: [
                    {
                      message: 'Please complete start date and end date',
                      validator: validateCustomRange,
                    },
                  ],
                })(
                  <DateRangePicker
                    size="large"
                    css={formStyle.inputGray}
                    disabledDate={disableDayAfter}
                  />
                )}
              </AntForm.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

AddSavedSearch.propTypes = {
  visible: PropTypes.bool,
  closeModal: PropTypes.func,
  searchOptions: PropTypes.shape({
    charts: PropTypes.array,
    categories: PropTypes.array,
    states: PropTypes.array,
  }),
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func,
    getFieldValue: PropTypes.func,
    resetFields: PropTypes.func,
    setFieldsValue: PropTypes.func,
    validateFieldsAndScroll: PropTypes.func,
  }),
  data: PropTypes.shape({}),
  handleSaveSearch: PropTypes.func,
  includeDates: PropTypes.bool,
};

export default AntForm.create()(AddSavedSearch);